<template>
    <div class="attachmentManager">
        <button type="button" @click="showModal = true" class="attachBtn" :class="{ 'hasFiles': hasAttachments, 'with-text': buttonText }">
            <b-icon-paperclip />
            <span v-if="buttonText" class="btnText">{{ buttonText }}</span>
            <span v-if="hasAttachments && !buttonText" class="attachCount">{{ totalAttachments }}</span>
        </button>

        <b-modal v-model="showModal" title="Gerenciar Anexos - Causa Raiz" size="lg" hide-footer>
            <div class="attachmentContent">
                <!-- Seção de Upload de Arquivos -->
                <div class="uploadSection">
                    <h6>Anexar Arquivos</h6>
                    <div class="uploadArea">
                        <button type="button" @click="triggerFileInput" class="uploadBtn">
                            <b-icon-cloud-upload />
                            <span>Selecionar Arquivos</span>
                        </button>
                        <input type="file" ref="fileInput" multiple :accept="allowedFileTypes"
                            style="display: none;" @change="onFileChange" />
                        <small class="text-muted">Formatos aceitos: PNG, JPG, PDF, DOC, DOCX (máx. 10MB total)</small>
                    </div>

                    <!-- Lista de Arquivos -->
                    <div v-if="localFiles.length > 0" class="fileList">
                        <div v-for="(file, idx) in localFiles" :key="`file-${idx}`" class="fileItem">
                            <button type="button" @click="downloadFile(file)" class="downloadFileBtn" title="Baixar arquivo">
                                <b-icon-arrow-down-circle-fill />
                            </button>
                            <div class="fileInfo">
                                <span class="fileName">{{ file.name }}</span>
                                <span class="fileSize">{{ formatFileSize(file.size) }}</span>
                            </div>
                            <button type="button" @click="removeFile(idx)" class="removeBtn">
                                <b-icon-x-circle-fill />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Seção de Links Externos -->
                <div class="linksSection">
                    <h6>Links Externos</h6>
                    <div class="linkInput">
                        <b-form-input v-model="newLinkUrl" placeholder="Cole o link aqui"
                            @keyup.enter="addLink"></b-form-input>
                        <b-form-input v-model="newLinkDescription" placeholder="Descrição (opcional)"
                            @keyup.enter="addLink"></b-form-input>
                        <button type="button" @click="addLink" class="addLinkBtn">
                            <b-icon-plus-circle />
                        </button>
                    </div>

                    <!-- Lista de Links -->
                    <div v-if="localLinks.length > 0" class="linkList">
                        <div v-for="(link, idx) in localLinks" :key="`link-${idx}`" class="linkItem">
                            <div class="linkInfo">
                                <b-icon-link45deg />
                                <a :href="ensureProtocol(link.url)" target="_blank" rel="noopener noreferrer">
                                    {{ link.description || link.url }}
                                </a>
                            </div>
                            <button type="button" @click="removeLink(idx)" class="removeBtn">
                                <b-icon-x-circle-fill />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Botões de Ação -->
                <div class="modalActions">
                    <button type="button" @click="cancelChanges" class="cancelBtn">Cancelar</button>
                    <button type="button" @click="saveChanges" class="saveBtn">Salvar</button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: "AttachmentManager",
    props: {
        files: {
            type: Array,
            default: () => []
        },
        links: {
            type: Array,
            default: () => []
        },
        editable: {
            type: Boolean,
            default: true
        },
        buttonText: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            showModal: false,
            localFiles: [],
            localLinks: [],
            newLinkUrl: "",
            newLinkDescription: "",
            allowedFileTypes: '.png,.jpg,.jpeg,.pdf,.doc,.docx',
            maxTotalSizeMB: 10,
        }
    },
    computed: {
        hasAttachments() {
            return this.localFiles.length > 0 || this.localLinks.length > 0;
        },
        totalAttachments() {
            return this.localFiles.length + this.localLinks.length;
        }
    },
    methods: {
        triggerFileInput() {
            if (!this.editable) return;
            this.$refs.fileInput.click();
        },

        onFileChange(event) {
            const files = Array.from(event.target.files);

            // Calcular tamanho total incluindo arquivos já existentes
            const existingSize = this.localFiles.reduce((total, file) => total + file.size, 0);
            const newSize = files.reduce((total, file) => total + file.size, 0);
            const totalSizeBytes = existingSize + newSize;
            const totalSizeMB = totalSizeBytes / (1024 * 1024);

            if (totalSizeMB > this.maxTotalSizeMB) {
                this.$bvToast.toast(
                    `O tamanho total dos arquivos (${totalSizeMB.toFixed(2)} MB) excede o limite de ${this.maxTotalSizeMB} MB. Recomendamos que você faça upload dos arquivos em um serviço externo (Google Drive, OneDrive, Dropbox, etc.) e adicione o link de compartilhamento na seção "Links Externos" abaixo.`,
                    {
                        title: "Arquivos muito grandes!",
                        variant: "warning",
                        toaster: 'b-toaster-bottom-left',
                        solid: true,
                        autoHideDelay: 10000
                    }
                );
                this.$refs.fileInput.value = "";
                return;
            }

            this.readFiles(files)
                .then(fileDataArray => {
                    this.localFiles.push(...fileDataArray);
                })
                .catch(error => {
                    this.$bvToast.toast(`Falha ao ler arquivos.`, {
                        title: "Arquivo inválido!",
                        variant: "warning",
                        toaster: 'b-toaster-bottom-left',
                        solid: true
                    });
                    console.error(error);
                });

            this.$refs.fileInput.value = "";
        },

        readFiles(files) {
            const promises = files.map(file => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        resolve({
                            name: file.name,
                            type: file.type,
                            size: file.size,
                            data: e.target.result
                        });
                    };
                    reader.onerror = (error) => {
                        reject(error);
                    };
                    reader.readAsDataURL(file);
                });
            });
            return Promise.all(promises);
        },

        removeFile(index) {
            if (!this.editable) return;
            this.localFiles.splice(index, 1);
        },

        addLink() {
            if (!this.editable) return;
            if (!this.newLinkUrl.trim()) {
                this.$bvToast.toast("Por favor, insira um link.", {
                    title: "Campo vazio",
                    variant: "warning",
                    toaster: 'b-toaster-bottom-left',
                    solid: true
                });
                return;
            }

            this.localLinks.push({
                url: this.newLinkUrl.trim(),
                description: this.newLinkDescription.trim()
            });

            this.newLinkUrl = "";
            this.newLinkDescription = "";
        },

        removeLink(index) {
            if (!this.editable) return;
            this.localLinks.splice(index, 1);
        },

        ensureProtocol(url) {
            if (!url) return '';
            const trimmedUrl = url.trim();

            // Se já tem protocolo, retorna como está
            if (/^https?:\/\//i.test(trimmedUrl)) {
                return trimmedUrl;
            }

            // Se não tem protocolo, adiciona https://
            return 'https://' + trimmedUrl;
        },

        downloadFile(file) {
            // Criar link temporário para download
            const link = document.createElement('a');
            link.href = file.data;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        },

        saveChanges() {
            this.$emit('update:files', [...this.localFiles]);
            this.$emit('update:links', [...this.localLinks]);
            this.$emit('change', {
                files: [...this.localFiles],
                links: [...this.localLinks]
            });
            this.showModal = false;
        },

        cancelChanges() {
            // Restaurar estado original
            this.localFiles = [...this.files];
            this.localLinks = [...this.links];
            this.newLinkUrl = "";
            this.newLinkDescription = "";
            this.showModal = false;
        },

        initializeData() {
            this.localFiles = [...this.files];
            this.localLinks = [...this.links];
        }
    },
    watch: {
        files: {
            handler() {
                this.localFiles = [...this.files];
            },
            deep: true
        },
        links: {
            handler() {
                this.localLinks = [...this.links];
            },
            deep: true
        }
    },
    mounted() {
        this.initializeData();
    }
}
</script>

<style scoped>
.attachmentManager {
    display: flex;
    align-items: center;
    justify-content: center;
}

.attachBtn {
    position: relative;
    background-color: #f8e4d9;
    border: 1px solid #FF5F00;
    border-radius: 0.3rem;
    color: #FF5F00;
    padding: 0.65rem 0.75rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    min-width: 15px;
    min-height: 30px;
    margin-bottom: 7px;
}

.attachBtn:hover {
    background-color: #FF5F00;
    color: white;
}

.attachBtn.hasFiles {
    background-color: #FF5F00;
    color: white;
}

.attachBtn.with-text {
    gap: 0.3rem;
    font-size: 1rem;
    padding: 0.3rem 0.4rem;
    border-radius: 0.2rem;
    font-weight: 500;
    background-color: #fff;
    color: #FF5F00;
    margin-bottom: 0;
    min-width: auto;
    min-height: auto;
}

.attachBtn.with-text:hover {
    background-color: #FF5F00;
    color: white;
}

.attachBtn .btnText {
    font-weight: 500;
}

.attachCount {
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: #D21724;
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: bold;
    border: 2px solid white;
}

.attachmentContent {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.uploadSection,
.linksSection {
    border: 1px solid #dee2e6;
    border-radius: 0.3rem;
    padding: 1rem;
}

.uploadSection h6,
.linksSection h6 {
    color: #046FDB;
    margin-bottom: 0.8rem;
    font-weight: 600;
}

.uploadArea {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.uploadBtn {
    background-color: #FF5F00;
    color: white;
    border: 1px solid #FF5F00;
    border-radius: 0.2rem;
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.uploadBtn:hover {
    background-color: #e55500;
}

.fileList,
.linkList {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 200px;
    overflow-y: auto;
}

.fileItem,
.linkItem {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background-color: #f8f9fa;
    border-radius: 0.2rem;
    border: 1px solid #dee2e6;
}

.downloadFileBtn {
    background: none;
    border: none;
    color: #008787;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 1.3rem;
    transition: all 0.2s;
    flex-shrink: 0;
}

.downloadFileBtn:hover {
    color: #006666;
    transform: scale(1.1);
}

.fileInfo,
.linkInfo {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
}

.fileName,
.linkInfo a {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #046FDB;
}

.linkInfo a {
    text-decoration: none;
}

.linkInfo a:hover {
    text-decoration: underline;
}

.fileSize {
    color: #6c757d;
    font-size: 0.8rem;
    white-space: nowrap;
}

.removeBtn {
    background: none;
    border: none;
    color: #D21724;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    transition: all 0.2s;
}

.removeBtn:hover {
    color: #a01319;
}

.linkInput {
    display: grid;
    grid-template-columns: 2fr 1fr auto;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.addLinkBtn {
    background-color: #008787;
    color: white;
    border: 1px solid #008787;
    border-radius: 0.2rem;
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.addLinkBtn:hover {
    background-color: #006666;
}

.modalActions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid #dee2e6;
}

.cancelBtn,
.saveBtn {
    padding: 0.5rem 1rem;
    border-radius: 0.2rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.cancelBtn {
    background-color: white;
    color: #008787;
    border: 1px solid #008787;
}

.cancelBtn:hover {
    background-color: #f0f9f9;
}

.saveBtn {
    background-color: #008787;
    color: white;
    border: 1px solid #008787;
}

.saveBtn:hover {
    background-color: #006666;
}
</style>

<style>
/* Previne "pulo" quando modal abre/fecha - força scrollbar sempre visível */
html {
    overflow-y: scroll;
}
</style>
